import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  FileText,
  File,
  Image,
  Download,
  Eye,
  RefreshCw,
  Upload,
  Trash2,
  Clock,
  CheckCircle2,
  Filter,
  Search,
  FolderOpen,
  ExternalLink,
  History,
  Plus,
  UserPlus,
  X,
  Lock,
  AlertTriangle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DashboardLayout from "../components/DashboardLayout";
import DocumentUploader from "../components/DocumentUploader";
import { EarnMoneyBanner } from "../components/Client/PlanSection";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { id: "passport", name: "Pasaporte" },
  { id: "id_card", name: "Identificación (INE/ID)" },
  { id: "employment_letter", name: "Carta de Trabajo" },
  { id: "pay_stubs", name: "Últimos Recibos de Sueldo" },
  { id: "work_contract", name: "Contrato Laboral" },
  { id: "previous_visa", name: "Visa Anterior" },
  { id: "bank_statements", name: "Estados de Cuenta" },
  { id: "income_proof", name: "Comprobante de Ingresos" },
  { id: "marriage_certificate", name: "Acta de Matrimonio" },
  { id: "birth_certificate", name: "Acta de Nacimiento" },
  { id: "address_proof", name: "Comprobante de Domicilio" },
  { id: "photo", name: "Fotografía" },
  { id: "property_deed", name: "Escrituras" },
  { id: "tax_return", name: "Declaración de Impuestos" },
  { id: "travel_itinerary", name: "Itinerario de Viaje" },
  { id: "invitation_letter", name: "Carta de Invitación" },
  { id: "other", name: "Otro" }
];

// Required documents for each person in the case
const REQUIRED_DOCUMENTS = [
  { id: "passport", name: "Pasaporte", description: "Pasaporte vigente con al menos 6 meses de validez", required: true },
  { id: "id_card", name: "Identificación (INE/ID)", description: "Identificación oficial vigente", required: true },
  { id: "photo", name: "Fotografía", description: "Foto tamaño pasaporte fondo blanco", required: true },
  { id: "employment_letter", name: "Carta de Trabajo", description: "Carta de tu empleador indicando puesto y antigüedad", required: true },
  { id: "pay_stubs", name: "Últimos Recibos de Sueldo", description: "Los últimos 3 recibos de nómina", required: true },
  { id: "work_contract", name: "Contrato Laboral", description: "Contrato de trabajo (si lo tienes)", required: false },
  { id: "bank_statements", name: "Estados de Cuenta", description: "Últimos 3 meses de estados de cuenta bancarios", required: false },
];

const FileIcon = ({ mimeType, className = "w-8 h-8" }) => {
  if (mimeType?.startsWith("image/")) {
    return <Image className={`${className} text-blue-500`} />;
  }
  if (mimeType === "application/pdf") {
    return <FileText className={`${className} text-red-500`} />;
  }
  return <File className={`${className} text-gray-500`} />;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const DocumentCard = ({ document, onDownload, onViewVersions, onReplace, onDelete }) => {
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDownload = async () => {
    // If we have a Cloudinary URL, open it directly
    if (document.cloudinary_url) {
      window.open(document.cloudinary_url, "_blank");
      toast.success("Abriendo documento...");
      return;
    }
    
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/documents/${document.id}/download`, {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement("a");
      link.href = url;
      link.setAttribute("download", document.stored_name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Documento descargado");
    } catch (error) {
      toast.error("Error al descargar");
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Estás seguro de eliminar este documento?")) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/documents/${document.id}`);
      toast.success("Documento eliminado");
      if (onDelete) onDelete(document.id);
    } catch (error) {
      toast.error("Error al eliminar documento");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-navy-100 p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="w-14 h-14 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0">
          <FileIcon mimeType={document.mime_type} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-navy-900 truncate">
            {document.document_type_name}
          </h4>
          <p className="text-sm text-navy-500 truncate">{document.file_name}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-navy-400">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDate(document.uploaded_at)}
            </span>
            <span>{formatFileSize(document.file_size)}</span>
            {document.version > 1 && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                v{document.version}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {(document.cloudinary_url || document.web_view_link) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(document.cloudinary_url || document.web_view_link, "_blank")}
              title="Ver documento"
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownload}
            disabled={downloading}
            title="Descargar"
          >
            {downloading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewVersions(document)}
            title="Ver versiones"
          >
            <History className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            disabled={deleting}
            title="Eliminar"
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            {deleting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Notes */}
      {document.notes && (
        <div className="mt-3 p-2 bg-navy-50 rounded-lg">
          <p className="text-xs text-navy-600">{document.notes}</p>
        </div>
      )}
    </motion.div>
  );
};

const DocumentHistory = () => {
  const { user, isTrialMode } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showUploader, setShowUploader] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(0); // Index of selected person
  const [showRequiredDocs, setShowRequiredDocs] = useState(true);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [newPersonName, setNewPersonName] = useState("");
  const [newPersonRelationship, setNewPersonRelationship] = useState("Cónyuge");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingPerson, setPendingPerson] = useState(null);
  const [requestingPayment, setRequestingPayment] = useState(false);
  
  // Check if user is in trial mode (plan_type === 'prueba')
  const isTrialUser = isTrialMode?.() || user?.plan_type === 'prueba';
  
  // Get authorized persons limit (default 1)
  const authorizedPersons = user?.authorized_persons || 1;
  const currentPersonsCount = caseData?.case_people?.length || 1;

  useEffect(() => {
    fetchDocuments();
    fetchCaseData();
  }, []);

  const fetchCaseData = async () => {
    try {
      const response = await axios.get(`${API}/cases/my-case`);
      setCaseData(response.data);
      // Initialize case_people if not exists
      if (!response.data.case_people || response.data.case_people.length === 0) {
        // Create default person (the main applicant)
        const userName = response.data.user_name || response.data.client_name || "Solicitante Principal";
        setCaseData(prev => ({
          ...prev,
          case_people: [{ id: "main", name: userName, relationship: "Solicitante Principal" }]
        }));
      }
    } catch (error) {
      console.log("No case data available");
    }
  };

  const addPersonToCase = async () => {
    if (!newPersonName.trim()) {
      toast.error("Ingresa el nombre de la persona");
      return;
    }
    
    // Check if user has reached their authorized persons limit
    if (currentPersonsCount >= authorizedPersons) {
      // Show payment modal instead of adding directly
      setPendingPerson({
        name: newPersonName.trim(),
        relationship: newPersonRelationship
      });
      setShowPaymentModal(true);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/cases/${caseData.id}/people`, {
        name: newPersonName.trim(),
        relationship: newPersonRelationship
      });
      
      if (response.data.success) {
        toast.success(`${newPersonName} agregado al caso`);
        // Update local state
        setCaseData(prev => ({
          ...prev,
          case_people: [...(prev.case_people || []), response.data.person]
        }));
        setNewPersonName("");
        setShowAddPerson(false);
      }
    } catch (error) {
      // Check if error is due to limit
      if (error.response?.data?.detail?.includes("límite") || error.response?.data?.detail?.includes("limit")) {
        setPendingPerson({
          name: newPersonName.trim(),
          relationship: newPersonRelationship
        });
        setShowPaymentModal(true);
      } else {
        toast.error(error.response?.data?.detail || "Error al agregar persona");
      }
      console.error(error);
    }
  };

  const requestAdditionalPersonPayment = async () => {
    if (!pendingPerson) return;
    
    setRequestingPayment(true);
    try {
      const response = await axios.post(`${API}/client/request-additional-person`, {
        name: pendingPerson.name,
        relationship: pendingPerson.relationship
      });
      
      if (response.data.payment_url) {
        toast.success("Redirigiendo al pago...");
        window.location.href = response.data.payment_url;
      } else if (response.data.payment_link) {
        toast.success("Redirigiendo al pago...");
        window.location.href = response.data.payment_link;
      } else {
        toast.error("Error al generar enlace de pago");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al solicitar pago");
    } finally {
      setRequestingPayment(false);
    }
  };

  const removePersonFromCase = async (personId) => {
    if (personId === "main") {
      toast.error("No puedes eliminar al solicitante principal");
      return;
    }
    
    try {
      await axios.delete(`${API}/cases/${caseData.id}/people/${personId}`);
      setCaseData(prev => ({
        ...prev,
        case_people: prev.case_people.filter(p => p.id !== personId)
      }));
      if (selectedPerson >= caseData.case_people.length - 1) {
        setSelectedPerson(0);
      }
      toast.success("Persona eliminada del caso");
    } catch (error) {
      toast.error("Error al eliminar persona");
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/documents/my-documents`);
      setDocuments(response.data);
    } catch (error) {
      if (error.response?.status !== 400) {
        toast.error("Error al cargar documentos");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = (data) => {
    setShowUploader(false);
    fetchDocuments();
  };

  const handleViewVersions = async (document) => {
    try {
      const response = await axios.get(`${API}/documents/${document.id}/versions`);
      // Could open a modal here to show versions
      toast.info(`Este documento tiene ${response.data.length} versión(es)`);
    } catch (error) {
      toast.error("Error al cargar versiones");
    }
  };

  const filteredDocuments = documents.filter(doc => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!doc.document_type_name?.toLowerCase().includes(query) && 
          !doc.file_name?.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (filterType !== "all" && doc.document_type !== filterType) {
      return false;
    }
    return true;
  });

  // Group by type
  const groupedDocuments = filteredDocuments.reduce((acc, doc) => {
    const type = doc.document_type_name || "Otros";
    if (!acc[type]) acc[type] = [];
    acc[type].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="document-history-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy-900">
              Historial de Documentos
            </h1>
            <p className="text-navy-500">
              Todos tus documentos subidos, organizados y seguros
            </p>
          </div>
          {isTrialUser ? (
            <Button
              disabled
              variant="outline"
              className="text-navy-400 cursor-not-allowed"
              data-testid="toggle-uploader-btn-disabled"
            >
              <Lock className="w-4 h-4 mr-2" />
              Subida Bloqueada
            </Button>
          ) : (
            <Button
              onClick={() => setShowUploader(!showUploader)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="toggle-uploader-btn"
            >
              {showUploader ? (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Ver Documentos
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Subir Documento
                </>
              )}
            </Button>
          )}
        </div>

        {/* Trial User Warning Banner */}
        {isTrialUser && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl"
          >
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">Modo Vista Previa</p>
                <p className="text-sm text-amber-700">
                  La subida de documentos está deshabilitada en cuentas de prueba. 
                  Contacta a tu asesor para activar tu cuenta completa.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Uploader */}
        <AnimatePresence>
          {showUploader && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6"
            >
              <DocumentUploader 
                onUploadSuccess={handleUploadSuccess} 
                personId={caseData?.case_people?.[selectedPerson]?.id}
                personName={caseData?.case_people?.[selectedPerson]?.name}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Required Documents Section */}
        {showRequiredDocs && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl border border-blue-200 p-5"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-navy-900">Documentos Requeridos</h3>
                  <p className="text-sm text-navy-500">Lista de documentos básicos que debes subir</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowRequiredDocs(false)}
                className="text-navy-400 hover:text-navy-600"
              >
                Ocultar
              </Button>
            </div>

            {/* Person Selector */}
            <div className="mb-4 p-3 bg-white rounded-lg border border-blue-100">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-navy-700">
                  👥 {caseData?.case_people?.length > 1 ? "Selecciona la persona:" : "Persona del caso:"}
                </p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowAddPerson(!showAddPerson)}
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Agregar Persona
                </Button>
              </div>
              
              {/* Add Person Form */}
              {showAddPerson && (
                <div className="mb-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <input
                      type="text"
                      placeholder="Nombre completo"
                      value={newPersonName}
                      onChange={(e) => setNewPersonName(e.target.value)}
                      className="px-3 py-2 border border-emerald-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <select
                      value={newPersonRelationship}
                      onChange={(e) => setNewPersonRelationship(e.target.value)}
                      className="px-3 py-2 border border-emerald-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="Cónyuge">Cónyuge</option>
                      <option value="Hijo/a">Hijo/a</option>
                      <option value="Padre/Madre">Padre/Madre</option>
                      <option value="Hermano/a">Hermano/a</option>
                      <option value="Otro familiar">Otro familiar</option>
                    </select>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={addPersonToCase}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      >
                        Agregar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowAddPerson(false)}
                        className="border-emerald-300"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* People List */}
              <div className="flex flex-wrap gap-2">
                {caseData?.case_people?.map((person, index) => {
                  // Check if this person is within the authorized limit
                  const isPaid = index < authorizedPersons;
                  
                  return (
                    <div key={person.id || index} className="flex items-center">
                      <Button
                        size="sm"
                        variant={selectedPerson === index ? "default" : "outline"}
                        onClick={() => {
                          if (isPaid) {
                            setSelectedPerson(index);
                          } else {
                            // Show payment modal for unpaid person
                            setPendingPerson({
                              name: person.name,
                              relationship: person.relationship
                            });
                            setShowPaymentModal(true);
                          }
                        }}
                        className={
                          !isPaid 
                            ? "border-red-300 text-red-600 hover:bg-red-50 opacity-75"
                            : selectedPerson === index 
                            ? "bg-blue-600 hover:bg-blue-700" 
                            : "border-blue-300 text-blue-700 hover:bg-blue-50"
                        }
                      >
                        {!isPaid && <Lock className="w-3 h-3 mr-1" />}
                        {person.name || `Persona ${index + 1}`}
                        {person.relationship && person.relationship !== "Solicitante Principal" && (
                          <span className="ml-1 text-xs opacity-75">({person.relationship})</span>
                        )}
                      </Button>
                      {person.id !== "main" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removePersonFromCase(person.id)}
                          className="ml-1 p-1 h-auto text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Info about paid/unpaid persons */}
              {currentPersonsCount > authorizedPersons && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    Tienes {currentPersonsCount - authorizedPersons} persona(s) pendiente(s) de pago. 
                    Las personas con <Lock className="w-3 h-3 inline mx-1" /> requieren pago para subir documentos.
                  </p>
                </div>
              )}
            </div>

            {/* Required Documents Checklist */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {REQUIRED_DOCUMENTS.map((doc) => {
                const personId = caseData?.case_people?.[selectedPerson]?.id || "main";
                const isUploaded = documents.some(
                  d => d.document_type === doc.id && (d.person_id === personId || (!d.person_id && personId === "main"))
                );
                
                return (
                  <div 
                    key={doc.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                      isUploaded 
                        ? "bg-emerald-50 border-emerald-200" 
                        : doc.required 
                          ? "bg-white border-amber-200" 
                          : "bg-white border-gray-200"
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isUploaded 
                        ? "bg-emerald-500" 
                        : doc.required 
                          ? "bg-amber-100" 
                          : "bg-gray-100"
                    }`}>
                      {isUploaded ? (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      ) : (
                        <span className={`text-xs font-bold ${doc.required ? "text-amber-600" : "text-gray-400"}`}>
                          {doc.required ? "!" : "?"}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium text-sm ${isUploaded ? "text-emerald-700" : "text-navy-800"}`}>
                          {doc.name}
                        </p>
                        {doc.required && !isUploaded && (
                          <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                            Requerido
                          </span>
                        )}
                        {!doc.required && (
                          <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">
                            Opcional
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-navy-500 mt-0.5">{doc.description}</p>
                    </div>
                    {!isUploaded && (
                      isTrialUser ? (
                        <div className="text-navy-300 flex-shrink-0">
                          <Lock className="w-4 h-4" />
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowUploader(true)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 flex-shrink-0"
                        >
                          <Upload className="w-4 h-4" />
                        </Button>
                      )
                    )}
                  </div>
                );
              })}
            </div>

            {/* Progress indicator */}
            <div className="mt-4 pt-4 border-t border-blue-200">
              {(() => {
                const personId = caseData?.case_people?.[selectedPerson]?.id || "main";
                const requiredDocs = REQUIRED_DOCUMENTS.filter(d => d.required);
                const uploadedRequired = requiredDocs.filter(doc => 
                  documents.some(d => d.document_type === doc.id && (d.person_id === personId || (!d.person_id && personId === "main")))
                ).length;
                const progress = Math.round((uploadedRequired / requiredDocs.length) * 100);
                
                return (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-blue-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-navy-700">
                      {uploadedRequired}/{requiredDocs.length} requeridos
                    </span>
                  </div>
                );
              })()}
            </div>
          </motion.div>
        )}

        {/* Show Required Docs Button (when hidden) */}
        {!showRequiredDocs && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRequiredDocs(true)}
            className="mb-4 border-blue-300 text-blue-600 hover:bg-blue-50"
          >
            <FileText className="w-4 h-4 mr-2" />
            Ver Documentos Requeridos
          </Button>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Total Documentos</p>
                <p className="text-2xl font-bold text-navy-900">{documents.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Tipos de Documento</p>
                <p className="text-2xl font-bold text-navy-900">
                  {Object.keys(groupedDocuments).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Cloudinary</p>
                <p className="text-lg font-bold text-navy-900">Conectado</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                placeholder="Buscar documento..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-56">
                <SelectValue placeholder="Filtrar por tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {DOCUMENT_TYPES.map(type => (
                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchDocuments}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Documents List */}
        {filteredDocuments.length > 0 ? (
          <div className="space-y-6">
            {Object.entries(groupedDocuments).map(([type, docs]) => (
              <div key={type}>
                <h3 className="font-medium text-navy-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {type}
                  <span className="text-sm text-navy-400">({docs.length})</span>
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {docs.map(doc => (
                    <DocumentCard
                      key={doc.id}
                      document={doc}
                      onViewVersions={handleViewVersions}
                      onDelete={(docId) => {
                        setDocuments(prev => prev.filter(d => d.id !== docId));
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border border-navy-100">
            <FolderOpen className="w-16 h-16 text-navy-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-navy-700 mb-2">
              No hay documentos
            </h3>
            <p className="text-navy-500 mb-4">
              {searchQuery || filterType !== "all"
                ? "No se encontraron documentos con esos filtros"
                : isTrialUser 
                  ? "La subida de documentos está deshabilitada en modo prueba"
                  : "Sube tu primer documento para comenzar"}
            </p>
            {!showUploader && !isTrialUser && (
              <Button onClick={() => setShowUploader(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Upload className="w-4 h-4 mr-2" />
                Subir Documento
              </Button>
            )}
            {isTrialUser && (
              <p className="text-sm text-amber-600 mt-2">
                <Lock className="w-4 h-4 inline mr-1" />
                Contacta a tu asesor para activar tu cuenta
              </p>
            )}
          </div>
        )}

        {/* Earn Money Banner - Only for clients */}
        <div className="mt-8">
          <EarnMoneyBanner userRole={user?.role} />
        </div>
      </div>

      {/* Payment Modal for Additional Person */}
      <AnimatePresence>
        {showPaymentModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setShowPaymentModal(false);
                setPendingPerson(null);
              }}
              className="fixed inset-0 bg-black/50 z-[100]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none"
            >
              <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden pointer-events-auto">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <UserPlus className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Persona Adicional</h2>
                    <p className="text-amber-100 text-sm">Requiere pago adicional</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <p className="text-amber-800 text-sm">
                    <strong>Tu plan actual incluye {authorizedPersons} persona(s).</strong>
                    <br />
                    Ya tienes {currentPersonsCount} persona(s) registrada(s).
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-2">Persona a agregar:</p>
                  <p className="font-semibold text-gray-900">{pendingPerson?.name}</p>
                  <p className="text-sm text-gray-500">({pendingPerson?.relationship})</p>
                </div>
                
                <p className="text-sm text-gray-600 mb-4">
                  Para agregar a <strong>{pendingPerson?.name}</strong> a tu caso, 
                  necesitas realizar un pago adicional por persona.
                </p>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowPaymentModal(false);
                      setPendingPerson(null);
                      setNewPersonName("");
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    onClick={requestAdditionalPersonPayment}
                    disabled={requestingPayment}
                  >
                    {requestingPayment ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Lock className="w-4 h-4 mr-2" />
                    )}
                    {requestingPayment ? "Procesando..." : "Pagar y Agregar"}
                  </Button>
                </div>
              </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
};

export default DocumentHistory;
